import { Button, Stack } from '@mantine/core';
import { useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useFamily } from 'src/clients/hooks/useFamily';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { useDeleteDataExport } from 'src/suitability-report/hooks/useDeleteDataExport';
import { useDeleteAgenda } from 'src/suitability-report/hooks/useDeleteAgenda';
import { useDeleteSuitabilityReport } from 'src/suitability-report/hooks/useDeleteSuitabilityReport';
import { useDeleteMeetingNote } from 'src/meeting-note/hooks/useDeleteMeetingNote';
import {
  UnifiedDocument,
  useAllClientDocuments,
} from 'src/suitability-report/hooks/useAllClientDocuments';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import PageLoader from 'src/UI/loader/PageLoader';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { dateMatchesSearch } from 'src/meetings/utils/date-utils';
import FileUploadLayout from 'src/data-capture/file-management/FileUploadLayout';
import EmptyView from 'src/data-capture/file-management/components/EmptyView';
import GenerateMenu from 'src/data-capture/file-management/components/GenerateMenu';
import GeneratedDocumentCard, {
  GeneratedDocumentType,
  GENERATED_DOCUMENT_TYPE_LABELS,
} from 'src/data-capture/file-management/components/GeneratedDocumentCard';

type GeneratedDocument = {
  id: string;
  clientId: string;
  displayName: string;
  documentType: GeneratedDocumentType;
  createdAt: string;
  fileId?: number;
};

type DocumentToDelete = {
  id: string;
  clientId: string;
  displayName: string;
  documentType: GeneratedDocumentType;
};

type FamilyGeneratedDocumentsProps = {
  documentTypeFilter?: GeneratedDocumentType;
};

const GENERATED_TYPES: GeneratedDocumentType[] = [
  'suitability-report',
  'meeting-agenda',
  'data-export',
  'meeting-note',
];

export default function FamilyGeneratedDocuments({
  documentTypeFilter,
}: FamilyGeneratedDocumentsProps) {
  const { familyId = '' } = useParams<{ familyId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { family } = useFamily(familyId);
  const familyName = family?.name;

  const contextClientId = searchParams.get('member') ?? family?.mainFamilyMember.clientId ?? null;

  const {
    documents: unifiedDocuments,
    isLoading,
    error,
    refetch,
  } = useAllClientDocuments({
    type: 'family',
    familyId,
  });
  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();

  const {
    deleteSuitabilityReport,
    error: deleteSuitabilityReportError,
    reset: resetDeleteSuitabilityReport,
    isLoading: isDeletingSuitabilityReport,
  } = useDeleteSuitabilityReport();
  const {
    deleteMeetingAgenda,
    error: deleteAgendaError,
    reset: resetDeleteAgenda,
    isLoading: isDeletingAgenda,
  } = useDeleteAgenda();
  const {
    deleteDataExport,
    error: deleteDataExportError,
    reset: resetDeleteDataExport,
    isLoading: isDeletingDataExport,
  } = useDeleteDataExport();
  const {
    deleteMeetingNote,
    error: deleteMeetingNoteError,
    reset: resetDeleteMeetingNote,
    isLoading: isDeletingMeetingNote,
  } = useDeleteMeetingNote();

  const [searchValue, setSearchValue] = useState('');
  const [documentToDelete, setDocumentToDelete] = useState<DocumentToDelete | null>(null);

  const openFlow = (flow: string) => {
    if (!contextClientId) return;
    navigate(`/client/${contextClientId}/${flow}`, { state: { fromPath: location.pathname } });
  };

  const handleDeletePress = (document: DocumentToDelete) => {
    resetDeleteSuitabilityReport();
    resetDeleteAgenda();
    resetDeleteDataExport();
    resetDeleteMeetingNote();
    setDocumentToDelete(document);
  };

  const deleteErrorByType: Record<string, { message: string } | null> = {
    'suitability-report': deleteSuitabilityReportError,
    'meeting-agenda': deleteAgendaError,
    'data-export': deleteDataExportError,
    'meeting-note': deleteMeetingNoteError,
  };
  const deleteError = documentToDelete ? deleteErrorByType[documentToDelete.documentType] : null;

  const handleDownload = async (fileId: number) => {
    try {
      const link = await createLink(fileId);
      window.open(link.url, '_blank');
    } catch (e) {
      notifyAndLogError(
        'Failed to download document',
        'An error occurred while trying to download the document. Please try again later.',
        e,
        { familyId, fileId },
      );
    }
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    const { clientId, id, documentType } = documentToDelete;
    try {
      switch (documentType) {
        case 'suitability-report':
          await deleteSuitabilityReport(clientId, id);
          break;
        case 'meeting-agenda':
          await deleteMeetingAgenda(id);
          break;
        case 'data-export':
          await deleteDataExport(clientId, id);
          break;
        case 'meeting-note':
          await deleteMeetingNote(clientId, id);
          break;
      }
    } catch (e) {
      notifyAndLogError(
        'Failed to delete document',
        'An error occurred while trying to delete the document. Please try again later.',
        e,
        { familyId, clientId, documentId: id, documentType },
      );
    }
    setDocumentToDelete(null);
    await refetch();
  };

  const title = documentTypeFilter
    ? GENERATED_DOCUMENT_TYPE_LABELS[documentTypeFilter]
    : 'Siesta AI generated';

  const documents: GeneratedDocument[] = unifiedDocuments
    .filter((doc): doc is UnifiedDocument & { documentType: GeneratedDocumentType } =>
      GENERATED_TYPES.includes(doc.documentType as GeneratedDocumentType),
    )
    .map(toGeneratedDocument)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const typedDocuments = documentTypeFilter
    ? documents.filter(doc => doc.documentType === documentTypeFilter)
    : documents;

  const filteredDocuments = searchValue.trim()
    ? typedDocuments.filter(doc => {
        const searchLower = searchValue.toLowerCase();
        return (
          doc.displayName.toLowerCase().includes(searchLower) ||
          GENERATED_DOCUMENT_TYPE_LABELS[doc.documentType].toLowerCase().includes(searchLower) ||
          dateMatchesSearch(doc.createdAt, searchLower)
        );
      })
    : typedDocuments;

  // On a filtered sub-tab the type is known, so "Generate" produces that type
  // directly; the unfiltered view keeps the dropdown to pick a type.
  const renderGenerateControl = (buttonText?: string) =>
    documentTypeFilter ? (
      <Button
        variant="primary"
        color="brand.6"
        size="md"
        onClick={() => openFlow(documentTypeFilter)}
        data-testid="generate-button"
      >
        {buttonText ?? 'Generate'}
      </Button>
    ) : (
      <GenerateMenu
        openSuitabilityFlow={() => openFlow('suitability-report')}
        openAgendaFlow={() => openFlow('meeting-agenda')}
        openDataExportFlow={() => openFlow('data-export')}
        openMeetingNote={() => openFlow('meeting-note')}
        buttonText={buttonText}
      />
    );

  if (error || creatingLinkFailed) {
    return <GenericErrorPage error={error || new Error('Failed to create download link')} />;
  }

  if (isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  if (typedDocuments.length === 0 && !isLoading) {
    return (
      <FileUploadLayout title={title} familyId={familyId} familyName={familyName}>
        <EmptyView
          title="Your AI-generated documents will appear here"
          subtitle="Generate suitability reports, meeting agendas, and data exports with Siesta AI. All your AI-created documents will be organised in one convenient location."
          customCta={renderGenerateControl('Generate new')}
        />
      </FileUploadLayout>
    );
  }

  return (
    <FileUploadLayout
      title={title}
      familyId={familyId}
      familyName={familyName}
      showSearch
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      searchResultCount={filteredDocuments.length}
      actions={renderGenerateControl()}
    >
      <Stack gap="xxs">
        {isLoading && filteredDocuments.length === 0 && <PageLoader absolute={false} />}
        {filteredDocuments.map(document => (
          <GeneratedDocumentCard
            key={`${document.documentType}-${document.id}`}
            displayName={document.displayName}
            documentType={document.documentType}
            createdAt={document.createdAt}
            fileId={document.fileId}
            onDownload={handleDownload}
            onDelete={() =>
              handleDeletePress({
                id: document.id,
                clientId: document.clientId,
                displayName: document.displayName,
                documentType: document.documentType,
              })
            }
          />
        ))}
      </Stack>

      <ConfirmDeleteModal
        opened={!!documentToDelete}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete document"
        message={`Are you sure you want to delete "${documentToDelete?.displayName}"? This action cannot be undone.`}
        error={deleteError}
        isLoading={
          isDeletingSuitabilityReport ||
          isDeletingAgenda ||
          isDeletingDataExport ||
          isDeletingMeetingNote
        }
      />
    </FileUploadLayout>
  );
}

function toGeneratedDocument(
  doc: UnifiedDocument & { documentType: GeneratedDocumentType },
): GeneratedDocument {
  return {
    id: doc.id,
    clientId: doc.clientId,
    documentType: doc.documentType,
    createdAt: doc.createdAt,
    fileId: doc.fileId,
    displayName: documentDisplayName(doc),
  };
}

function documentDisplayName(
  doc: UnifiedDocument & { documentType: GeneratedDocumentType },
): string {
  if (doc.file?.fileName) return doc.file.fileName;
  if (doc.documentType === 'data-export' && doc.configDisplayName) return doc.configDisplayName;
  return GENERATED_DOCUMENT_TYPE_LABELS[doc.documentType];
}
